// Right-hand detail pane (or, on mobile, the full screen after picking a row).
// Read-only summary plus the destructive/edit actions for the selected category.

import { Link } from "@tanstack/react-router";
import { FolderOpen, Pencil, Plus, Trash2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { findCategory, type Category } from "./types";

interface Props {
  cats: Category[];
  selectedId: string | null;
  canMutate: boolean;
  onEdit: (cat: Category) => void;
  onAddChild: (parentId: string) => void;
  onDelete: (cat: Category) => void;
  /** Mobile-only back button (renders only when provided). */
  onBack?: () => void;
}

export function CategoryDetailPanel({
  cats,
  selectedId,
  canMutate,
  onEdit,
  onAddChild,
  onDelete,
  onBack,
}: Props) {
  const cat = selectedId ? findCategory(cats, selectedId) : null;
  const parent = cat?.parentId ? findCategory(cats, cat.parentId) : null;

  if (!cat) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground">
        <FolderOpen size={36} className="opacity-50" />
        <p className="text-sm">Chọn một danh mục để xem chi tiết.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {onBack && (
        <div className="border-b p-2 md:hidden">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft size={16} />
            <span>Quay lại</span>
          </Button>
        </div>
      )}

      <div className="flex-1 space-y-5 overflow-y-auto p-5">
        <header className="space-y-1">
          <h2 className="text-xl font-semibold leading-tight">{cat.name}</h2>
          <p className="text-xs text-muted-foreground">
            <Link
              to="/products"
              search={{ category: cat.id }}
              className="font-medium text-primary hover:underline"
              title="Xem danh sách sản phẩm thuộc danh mục này"
            >
              {cat.productCount} sản phẩm
            </Link>
            {cat.children.length > 0 && ` · ${cat.children.length} danh mục con`}
          </p>
        </header>

        <dl className="grid grid-cols-[88px,1fr] gap-y-2 text-sm">
          <dt className="text-muted-foreground">Slug</dt>
          <dd className="font-mono text-xs">{cat.slug}</dd>

          <dt className="text-muted-foreground">Cha</dt>
          <dd>{parent ? parent.name : <span className="text-muted-foreground">— Gốc —</span>}</dd>

          <dt className="text-muted-foreground">Trạng thái</dt>
          <dd>{cat.isActive ? "Đang hoạt động" : "Đã ẩn"}</dd>
        </dl>

        {canMutate && (
          <div className="flex flex-wrap gap-2 border-t pt-4">
            <Button size="sm" onClick={() => onEdit(cat)}>
              <Pencil size={14} />
              <span>Sửa</span>
            </Button>
            <Button size="sm" variant="outline" onClick={() => onAddChild(cat.id)}>
              <Plus size={14} />
              <span>Thêm con</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onDelete(cat)}
            >
              <Trash2 size={14} />
              <span>Xoá</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
